import * as React from 'react'
import { Label, skeletonBlock } from './formParts'

// Chargebee mounts its card iframes into these elements, so they stay in the
// page (merely invisible) while the component loads.
export const CARD_FIELD_IDS = {
    number: 'cb-card-number',
    expiry: 'cb-card-expiry',
    cvv: 'cb-card-cvv',
}

// The card fields are Chargebee iframes: they paint their own surface, so the
// wrapper only draws the border and the focus ring, never a background that
// would show as a differently-coloured rim around the iframe.
const fieldBox =
    'cb-field tw-min-h-[44px] tw-rounded-lg tw-border tw-border-solid tw-border-border tw-px-3 tw-py-2.5'

function CardFieldsSkeleton() {
    return (
        <div
            className="tw-flex tw-flex-col tw-gap-2"
            role="status"
            aria-label="Loading card fields"
            data-testid="payment-loading"
        >
            <Label>Card</Label>
            <div className={skeletonBlock} />
            <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                <div className={skeletonBlock} />
                <div className={skeletonBlock} />
            </div>
        </div>
    )
}

// `card` is the state of useCardComponent.
export default function CardFields({ card }) {
    const loading = card.status === 'idle' || card.status === 'loading'
    return (
        <div className="tw-relative">
            {loading ? <CardFieldsSkeleton /> : null}
            <div
                className={
                    loading
                        ? 'tw-invisible tw-absolute tw-inset-x-0 tw-top-0 tw-flex tw-flex-col tw-gap-2'
                        : 'tw-flex tw-flex-col tw-gap-2'
                }
                aria-hidden={loading || undefined}
                data-testid="payment-card"
            >
                <Label>Card</Label>
                <div id={CARD_FIELD_IDS.number} className={fieldBox} />
                <div className="tw-grid tw-grid-cols-2 tw-gap-2">
                    <div id={CARD_FIELD_IDS.expiry} className={fieldBox} />
                    <div id={CARD_FIELD_IDS.cvv} className={fieldBox} />
                </div>
                {card.status === 'failed' ? (
                    <p
                        className="tw-m-0 tw-text-sm tw-text-destructive"
                        role="alert"
                    >
                        {card.error || 'The card fields could not be loaded.'}
                    </p>
                ) : null}
                {card.error && card.status === 'ready' ? (
                    <p
                        className="tw-m-0 tw-text-xs tw-text-destructive"
                        data-testid="payment-card-error"
                    >
                        {card.error}
                    </p>
                ) : null}
                {card.status === 'ready' && !card.complete && !card.error ? (
                    <p className="tw-m-0 tw-text-xs tw-text-muted-foreground">
                        Enter your card details to continue.
                    </p>
                ) : null}
            </div>
        </div>
    )
}
